import { useMutation, useQuery } from "@tanstack/react-query";
import { Download, Eye, Loader2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import EditRecordModal from "@/features/records/EditRecordModal";
import { ApiError } from "@/lib/api/client";
import { getJob, type CsvType } from "@/lib/api/jobs";
import { downloadRequestCsv, getRequestCsvPreview } from "@/lib/api/requests";
import { cn } from "@/lib/cn";

import CsvPreviewModal from "../jobs/CsvPreviewModal";
import DeliveryQaPanel from "../jobs/DeliveryQaPanel";
import { useRequestQa } from "../jobs/useQa";

interface Props {
  requestId: string;
  successCount: number;
  rejectedCount: number;
}

interface Variant {
  type: CsvType;
  i18nKey: "delivery_ok" | "delivery_rejected" | "review_ok" | "review_rejected";
  showWhen: "ok" | "rejected";
}

const VARIANTS: Variant[] = [
  { type: "delivery", i18nKey: "delivery_ok", showWhen: "ok" },
  { type: "rejected_delivery", i18nKey: "delivery_rejected", showWhen: "rejected" },
  { type: "review", i18nKey: "review_ok", showWhen: "ok" },
  { type: "rejected_review", i18nKey: "review_rejected", showWhen: "rejected" },
];

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function RequestCsvSection({ requestId, successCount, rejectedCount }: Props) {
  const { t } = useTranslation();
  const [showPreview, setShowPreview] = useState(false);
  const qa = useRequestQa(requestId);

  // QA 항목 클릭 → 해당 작업 상세를 지연 로드해 케이스 편집 모달을 연다.
  const [editing, setEditing] = useState<{ jobId: string; fileId: string } | null>(null);
  const editJob = useQuery({
    queryKey: ["job", editing?.jobId],
    queryFn: () => getJob(editing?.jobId ?? ""),
    enabled: !!editing?.jobId,
  });
  const editingFile = editing
    ? (editJob.data?.files.find((f) => f.id === editing.fileId) ?? null)
    : null;

  const download = useMutation<{ type: CsvType }, ApiError | Error, { type: CsvType }>({
    mutationFn: async ({ type }) => {
      const { blob, filename } = await downloadRequestCsv(requestId, type);
      triggerDownload(blob, filename);
      return { type };
    },
  });

  // 다운로드 차단은 풀었다(FB 2026-09-15). block/warn 상태값·표시는 유지하되
  // 다운로드 자체는 막지 않고, 확인창을 거치면 진행된다.
  const isEnabled = (v: Variant) => (v.showWhen === "ok" ? successCount > 0 : rejectedCount > 0);
  const anyData = successCount > 0 || rejectedCount > 0;

  // 납품용(ok) 다운로드 시 QA 이슈(정책 위반/정합성/형식 등)가 있으면 확인 후 진행.
  const handleDownload = (v: Variant) => {
    const hasIssues = !!qa.data && (qa.data.blocking || qa.data.has_warnings);
    if (v.showWhen === "ok" && hasIssues && !window.confirm(t("qa.downloadConfirm"))) {
      return;
    }
    download.mutate({ type: v.type });
  };

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-800">{t("requests.detail.csv.title")}</h2>
          <p className="mt-0.5 text-xs text-slate-500">{t("requests.detail.csv.description")}</p>
        </div>
        <button
          type="button"
          onClick={() => setShowPreview(true)}
          disabled={!anyData}
          className={cn(
            "inline-flex shrink-0 items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition",
            anyData
              ? "border-slate-300 text-slate-700 hover:bg-slate-50"
              : "cursor-not-allowed border-slate-100 text-slate-300",
          )}
        >
          <Eye className="h-3.5 w-3.5" />
          {t("jobs.detail.csv.previewButton")}
        </button>
      </div>

      {anyData && (
        <div className="mt-3">
          <DeliveryQaPanel
            report={qa.data}
            isLoading={qa.isLoading}
            isError={qa.isError}
            onEditItem={(it) => setEditing({ jobId: it.job_id, fileId: it.file_id })}
          />
        </div>
      )}

      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {VARIANTS.map((v) => {
          const enabled = isEnabled(v);
          const isPending = download.isPending && download.variables?.type === v.type;
          return (
            <button
              key={v.type}
              type="button"
              onClick={() => handleDownload(v)}
              disabled={!enabled || download.isPending}
              className={cn(
                "flex items-center justify-between rounded-md border px-3 py-2.5 text-left text-xs transition",
                enabled
                  ? "border-slate-200 hover:border-blue-300 hover:bg-blue-50/50"
                  : "border-slate-100 bg-slate-50 text-slate-400",
                download.isPending && "cursor-wait",
              )}
            >
              <div className="min-w-0">
                <p className={cn("font-medium", enabled ? "text-slate-800" : "text-slate-400")}>
                  {t(`jobs.detail.csv.variants.${v.i18nKey}.label`)}
                </p>
                <p className="mt-0.5 text-[10px] text-slate-400">
                  {t(`jobs.detail.csv.variants.${v.i18nKey}.hint`)}
                </p>
              </div>
              {isPending ? (
                <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
              ) : (
                <Download className={cn("h-4 w-4", enabled ? "text-blue-500" : "text-slate-300")} />
              )}
            </button>
          );
        })}
      </div>

      {download.isError && (
        <p
          role="alert"
          className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
        >
          {t("jobs.detail.csv.errorPrefix")}: {download.error.message}
        </p>
      )}

      {showPreview && (
        <CsvPreviewModal
          successCount={successCount}
          rejectedCount={rejectedCount}
          cacheKey={`request:${requestId}`}
          fetchGroupedPreview={(type) => getRequestCsvPreview(requestId, type)}
          onClose={() => setShowPreview(false)}
        />
      )}

      {/* QA 항목 클릭 편집 — 작업 상세 로딩 중 스피너, 로딩 완료 시 편집 모달. */}
      {editing && !editingFile && editJob.isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <Loader2 className="h-6 w-6 animate-spin text-white" />
        </div>
      )}
      {editingFile && editing && (
        <EditRecordModal
          jobId={editing.jobId}
          file={editingFile}
          onClose={() => {
            setEditing(null);
            void qa.refetch();
          }}
        />
      )}
    </section>
  );
}
